import { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidenavbar from '../../Components/Sidenavbar/Sidenavbar';
import UserMenu from '../../Components/UserMenu/UserMenu';
import { fetchData, postData, putData } from '../../Services/apiService';
import { useNavigate, useParams } from 'react-router-dom';

const makeKey = (name, role) =>
  `${(name || "").toString().trim().toLowerCase()}__${(role || "").toString().trim().toLowerCase()}`;

const hasDuplicateByNameRole = (arr) => {
  const seen = new Set();
  for (const u of arr) {
    if (!u.unitName || !u.unitRole) continue;
    const key = makeKey(u.unitName, u.unitRole);
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
};

function EditUser() {
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);

  const [units, setUnits] = useState([]);

  const [roles, setRoles] = useState([]); 
  const [unitsCPOptions, setUnitsCPOptions] = useState([]); 
  const [nameToIds, setNameToIds] = useState({});         
  const [idToName, setIdToName] = useState({});            

  const navigate = useNavigate();

  // --- Helpers ---
  function normalizeUnitsByNameRole(list) {
    const map = new Map(); 
    for (const u of list) {
      const name = u.unitName || '';
      const role = u.unitRole || '';
      const key = `${name}|${role}`;

      if (!map.has(key)) {
        map.set(key, { ...u });
      } else {
        const prev = map.get(key);
        if (role === 'aprobador') {
          const p = Number(prev.maxAmount ?? 0);
          const c = Number(u.maxAmount ?? 0);
          prev.maxAmount = Math.max(p, c);
        }
        map.set(key, prev);
      }
    }
    return Array.from(map.values());
  }

 
  useEffect(() => {
    (async () => {
      await fetchUnitsCPData();
      await fetchRolesData();
      await fetchUserData();
    })();
  }, []);

  const fetchRolesData = async () => {
    try {
      const data = await fetchData('AppUser/GetAppUserRoles');
      setRoles(data.data || []);
    } catch (error) {
      console.error('Error fetching roles data:', error);
    }
  };

  const fetchUnitsCPData = async () => {
    try {
      const data = await postData('BusinessUnit/GetBusinessUnit', {});
     
      const _nameToIds = {};
      const _idToName = {};

      for (const u of (data?.data || [])) {
        const name = u.nameBusinessUnit;
        const id = u.idBusinessUnit;
        _idToName[id] = name;
        if (!_nameToIds[name]) _nameToIds[name] = [];
        _nameToIds[name].push(id);
      }

      const uniqueNameOptions = Object.keys(_nameToIds)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name }));

      setIdToName(_idToName);
      setNameToIds(_nameToIds);
      setUnitsCPOptions(uniqueNameOptions);
    } catch (error) {
      console.error('Error fetching BUs:', error);
    }
  };

  const fetchUserData = async () => {
  try {
    const data = await postData('AppUser/GetAppUserById/', userId);
    setUserData(data.data);
  } catch (error) {
    console.error('Error fetching user data:', error);
    toast.error('Error al cargar los datos del usuario');
  }
};

useEffect(() => {
  if (!userData || !userData.businessUnits || Object.keys(idToName).length === 0) return;

  const unitsData = userData.businessUnits.flatMap((unit, idxUnit) =>
    (unit.appUserRoles || []).map((role, idxRole) => ({
      id: `${unit.id}-${role.id}-${idxUnit}-${idxRole}`,   
      unitName: idToName[unit.id] || null,                 
      unitRole: (role.name || '').toLowerCase(),
      maxAmount: unit.topAmount ?? ''
    }))
  );

  setUnits(normalizeUnitsByNameRole(unitsData));
}, [userData, idToName]);

  
  const displayUnits = useMemo(() => {
    const map = new Map(); 
    for (const u of units) {
      const name = u.unitName || '';
      const role = u.unitRole || '';
      const key = `${name}|${role}`;

      if (!map.has(key)) {
        map.set(key, {
          id: key,
          unitName: name,
          unitRole: role,
          maxAmount: role === 'aprobador' ? Number(u.maxAmount || 0) : '',
        });
      } else if (role === 'aprobador') {
        const curr = map.get(key);
        const candidate = Number(u.maxAmount || 0);
        curr.maxAmount = Math.max(Number(curr.maxAmount || 0), candidate);
        map.set(key, curr);
      }
    }
    return Array.from(map.values());
  }, [units]);

  const handleDisplayChange = (displayId, field, value) => {
    const edited = displayUnits.map(item => {
      if (item.id !== displayId) return item;

      if (field === 'unitRole' && value === 'ejecutor') {
        return { ...item, unitRole: value, maxAmount: '' };
      }
      if (field === 'maxAmount') {
        return { ...item, maxAmount: value };
      }
      if (field === 'unitName') {
        return { ...item, unitName: value };
      }
      return { ...item, [field]: value };
    });

    const rebuiltUnits = edited.map(it => ({
      id: it.id,
      unitName: it.unitName,
      unitRole: it.unitRole,
      maxAmount: it.unitRole === 'aprobador' ? it.maxAmount : ''
    }));

    if (hasDuplicateByNameRole(rebuiltUnits)) {
      toast.warn('Esa Unidad CP + Rol ya existe.', {
        position: "top-center", autoClose: true, hideProgressBar: true, theme: "dark"
      });
      return; 
    }

    setUnits(normalizeUnitsByNameRole(rebuiltUnits));
  };

  const addUnit = () => {
    setUnits(prev => normalizeUnitsByNameRole([
      ...prev,
      { id: `tmp-${Date.now()}`, unitName: null, unitRole: '', maxAmount: '' }
    ]));
  };

  const handleGoBack = () => navigate(-1);

  const updateUser = async () => {
    if (hasDuplicateByNameRole(units)) {
      toast.error('No puedes repetir la misma Unidad CP con el mismo rol (Aprobador/Ejecutor).', {
        position: "top-center", autoClose: true, hideProgressBar: true, theme: "dark"
      });
      return;
    }

    for (const u of units) {
      if (!u.unitName || !u.unitRole) {
        toast.error('Cada Unidad CP debe tener nombre y rol.', {
          position: "top-center", autoClose: true, hideProgressBar: true, theme: "dark"
        });
        return;
      }
      if (u.unitRole === 'aprobador') {
        const n = Number(u.maxAmount);
        if (Number.isNaN(n) || n < 0) {
          toast.error('El Monto Máximo debe ser un número válido (>= 0) para Aprobador.', {
            position: "top-center", autoClose: true, hideProgressBar: true, theme: "dark"
          });
          return;
        }
      }
    }
    try {
      const userName = document.getElementById('userName').value;
      const userMail = document.getElementById('userMail').value;
      const userPass = document.getElementById('userPass').value;

      if (!userName || !userMail) {
        toast.error('Todos los campos son obligatorios', {
          position: "top-center",
          autoClose: true,
          hideProgressBar: true,
          theme: "dark",
        });
        return;
      }

      const expanded = units.flatMap((unit) => {
        const ids = nameToIds[unit.unitName] || [];
        const isEjecutor = unit.unitRole === 'ejecutor';
        const topAmount = isEjecutor ? 0 : Number(unit.maxAmount || 0);
        const roleId = isEjecutor ? 2 : 1;
        return ids.map((id) => ({
          id,
          topAmount,
          appUserRoles: [{ id: roleId }],
        }));
      });

      const seen = new Set();
      const businessUnits = expanded.filter((item) => {
        const key = `${item.id}|${item.topAmount}|${item.appUserRoles[0].id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const payload = {
        name: userName,
        email: userMail,
        password: userPass,
        isAdmin: userData?.isAdmin,
        id: userData?.id,
        businessUnits,
      };

      const response = await putData('AppUser/', JSON.stringify(payload));
      if (response.succes) {
        toast.success('Usuario editado correctamente', {
          position: "top-center",
          autoClose: true,
          hideProgressBar: true,
          theme: "dark",
        });
        navigate('/AdminUsers');
      } else {
        toast.error('Error al editar el usuario', {
          position: "top-center",
          autoClose: true,
          hideProgressBar: true,
          theme: "dark",
        });
      }
    } catch (error) {
      console.error('Error al editar usuario:', error);
      toast.error('Ocurrió un error inesperado', {
        position: "top-center",
        autoClose: true,
        hideProgressBar: true,
        theme: "dark",
      });
    }
  };

  const handleSelectAll = (e) => {
    e.target.select();
  };

  return (
    <div className="container-fluid px-0 m-0 d-flex flex-column flex-md-row">
      <Sidenavbar userType="admin" />
      <div className="container-fluid px-16">
        <UserMenu userType="admin" />
        <ToastContainer />

        {/* Editar Usuario */}
        <div className="row mt-40">
          <div className="col-12 col-md-6 col-lg-3">
            <a href="#">
              <button className="btn -text" onClick={handleGoBack}>
                <i className="fa-solid fa-arrow-left"></i> Regresar
              </button>
            </a>
          </div>
        </div>

        <div className="row centered">
          <div className="col-12 col-md-8">
            <div className="card -walnut">
              <div className="row">
                <div className="col">
                  <p className="h2">Editar usuario</p>
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-md-4 mt-3">
                  <label htmlFor="userName" className="form-label">Usuario</label>
                  <input
                    type="text"
                    className="form-control -input"
                    id="userName"
                    value={userData?.name || ''}
                    onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                    placeholder="Ej. Juan Flores"
                  />
                </div>

                <div className="col-12 col-md-4 mt-3">
                  <label htmlFor="userMail" className="form-label">Correo</label>
                  <input
                    type="email"
                    className="form-control -input"
                    id="userMail"
                    value={userData?.email || ''}
                    onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                    placeholder="Ej. jflores@correo.com"
                  />
                </div>

                <div className="col-12 col-md-4 mt-3">
                  <label htmlFor="userPass" className="form-label">Contraseña</label>
                  <input
                    type="password"
                    className="form-control -input"
                    id="userPass"
                    placeholder="Nueva contraseña"
                  />
                </div>

                {/* Checkbox isAdmin */}
                <div className="col-12 col-md-4 mt-3">
                  <label className="form-label">¿Es administrador?</label>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isAdminCheckbox"
                      checked={userData?.isAdmin === 2}
                      onChange={(e) =>
                        setUserData({
                          ...userData,
                          isAdmin: e.target.checked ? 2 : 1
                        })
                      }
                    />
                    <label className="form-check-label" htmlFor="isAdminCheckbox">
                      Activar la administración de documentos
                    </label>
                  </div>
                </div>

                <div className="row">
                  <div className="col">
                    <p className="h3">Asignar unidades de negocio</p>
                  </div>
                </div>

                <div className="row">
                  {displayUnits.map((unit) => (
                    <div key={unit.id} className="col-12 col-md-4 align-content-top mt-3">
                      <div className='card'>
                        <div className='row'>
                          <div className="col-6 col-md-12 mt-3 align-content-top">
                            <label htmlFor={`unitName-${unit.id}`} className="form-label">Unidad CP</label>
                            <Select
                              id={`unitName-${unit.id}`}
                              options={unitsCPOptions}
                              value={unit.unitName ? { value: unit.unitName, label: unit.unitName } : null}
                              onChange={(opt) => handleDisplayChange(unit.id, 'unitName', opt?.value || null)}
                              placeholder="Selecciona una unidad"
                            />
                            {!unit.unitName && (
                              <label className="form-label -warning">Debes completar este dato</label>
                            )}
                          </div>

                          {/* Radio Buttons */}
                          <div className="col-6 col-md-12 mt-3">
                            <div className="d-flex">
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name={`unitRole-${unit.id}`}
                                  id={`aprobador-${unit.id}`}
                                  value="aprobador"
                                  checked={unit.unitRole === 'aprobador'}
                                  onChange={(e) => handleDisplayChange(unit.id, 'unitRole', e.target.value)}
                                />
                                <label className="form-check-label" htmlFor={`aprobador-${unit.id}`}>
                                  Aprobador
                                </label>
                              </div>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name={`unitRole-${unit.id}`}
                                  id={`ejecutor-${unit.id}`}
                                  value="ejecutor"
                                  checked={unit.unitRole === 'ejecutor'}
                                  onChange={(e) => handleDisplayChange(unit.id, 'unitRole', e.target.value)}
                                />
                                <label className="form-check-label" htmlFor={`ejecutor-${unit.id}`}>
                                  Ejecutor
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-6 col-md-6 mt-3 align-content-top">
                            <label htmlFor={`maxAmount-${unit.id}`} className="form-label">Monto Máximo</label>
                            <input
                              type="number"
                              className="form-control -input"
                              id={`maxAmount-${unit.id}`}
                              value={unit.maxAmount ?? ''}
                              onChange={(e) => handleDisplayChange(unit.id, 'maxAmount', e.target.value)}
                              disabled={unit.unitRole === 'ejecutor'}
                              onFocus={handleSelectAll}
                              onClick={handleSelectAll}
                            />
                            {unit.unitRole === 'aprobador' && (!unit.maxAmount && unit.maxAmount !== 0) && (
                              <label className="form-label -warning">Debes completar este dato</label>
                            )}
                          </div>

                          <div className="col-12 mt-3 text-end">
                            <button
                              className="btn btn-danger"
                              onClick={() =>
                                setUnits(prev =>
                                  prev.filter(u =>
                                    !(
                                      (u.unitName || '') === (unit.unitName || '') &&
                                      (u.unitRole || '') === (unit.unitRole || '')
                                    )
                                  )
                                )
                              }
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="row mt-40 justify-content-center">
                  <div className="col-12 col-md-6">
                    <button className="btn -secondary" onClick={addUnit}>
                      Agregar Unidad CP
                    </button>
                  </div>
                </div>
              </div>

              <hr />

              <div className="row mt-40 justify-content-center">
                <div className="col-12 col-md-6">
                  <a href="/AdminUsers">
                    <button className="btn -secondary">Cancelar</button>
                  </a>
                </div>
                <div className="col-12 col-md-6">
                  <button className="btn -primary" onClick={updateUser}>
                    Guardar Usuario
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default EditUser;
